import fetchWithTimeout from '../utils/fetchWithTimeout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mapSort = (sortBy) => {
  if (!sortBy || sortBy === 'newest') return null;
  if (sortBy === 'cheapest' || sortBy === 'price_low') return 'price_asc';
  if (sortBy === 'expensive' || sortBy === 'price_high') return 'price_desc';
  return sortBy;
};

export function buildAdsQueryParams({
  pageNum = 1,
  pageSize = 20,
  selectedCategory = 'all',
  selectedSubcategory = null,
  searchQuery = '',
  sortBy = 'newest',
  priceRange = { min: '', max: '' },
  locationFilter = '',
  country = '',
  city = '',
  region = '',
  area = '',
  locationStrict = false,
  deliveryAvailable = false,
  excludeFarmForAll = true,
  strict = true,
}) {
  const params = new URLSearchParams();
  params.append('limit', pageSize);
  params.append('skip', (pageNum - 1) * pageSize);

  const trimmedSearch = (searchQuery || '').trim();

  if (excludeFarmForAll && (selectedCategory === 'all' || selectedCategory === 'recommended')) {
    params.append('exclude_farm', '1');
  }

  if (selectedCategory === 'recommended' && !trimmedSearch) {
    params.append('sort', 'views');
  } else if (selectedCategory && selectedCategory !== 'all') {
    params.append('category', selectedCategory);
  }

  // Subcategory filter — strict match
  if (selectedSubcategory && selectedSubcategory !== 'all') {
    params.append('subcategory', selectedSubcategory);
  }

  if (trimmedSearch) {
    params.append('search', trimmedSearch);
    if (strict) {
      params.append('strict', '1');
    }
  }

  const mappedSort = mapSort(sortBy);
  if (mappedSort && !(selectedCategory === 'recommended' && !trimmedSearch)) {
    params.append('sort', mappedSort);
  }

  if (priceRange?.min) params.append('min_price', priceRange.min);
  if (priceRange?.max) params.append('max_price', priceRange.max);

  // Country filter (unified location system)
  const trimmedCountry = (country || '').trim().toLowerCase();
  if (trimmedCountry) {
    params.append('country', trimmedCountry);
  }

  // City filter (unified location system)
  const trimmedCity = (city || '').trim().toLowerCase();
  if (trimmedCity) {
    params.append('city', trimmedCity);
  }

  // iter322 Phase B — Region / area narrowing (optional). Sent alongside
  // country/city when the buyer picked deeper from LocationPickerSheet.
  const trimmedRegion = (region || '').trim();
  if (trimmedRegion) {
    params.append('region', trimmedRegion);
  }
  const trimmedArea = (area || '').trim();
  if (trimmedArea) {
    params.append('area', trimmedArea);
  }

  // iter322 Phase B — Strict manual-location mode. Backend rejects ads
  // whose structured country/city don't match. Without this flag, the
  // backend falls back to broad city-name regex matches (legacy mode).
  if (locationStrict && trimmedCountry) {
    params.append('location_strict', 'true');
  }

  // iter328 — Delivery availability filter. When true, backend
  // returns only ads where the seller flagged delivery_available=true.
  if (deliveryAvailable) {
    params.append('delivery_available', 'true');
  }

  // Legacy location filter (text search)
  const trimmedLocation = (locationFilter || '').trim();
  if (trimmedLocation.length >= 2) {
    params.append('location', trimmedLocation);
  }

  return params;
}

export async function searchAds(options = {}) {
  const params = buildAdsQueryParams(options);
  const response = await fetchWithTimeout(`${API_URL}/api/ads?${params.toString()}`);
  if (!response.ok) {
    throw new Error('search failed');
  }
  return response.json();
}
